import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getOrderedMediaCandidates, rememberMediaPriority } from 'lib/redundant-media';

const noop = () => {};

export default function ResilientImage({
  src,
  backupSrc = null,
  alt = '',
  preferenceKey = 'global:image',
  timeoutMs = 7000,
  onLoad = noop,
  onError = noop,
  ...props
}) {
  const candidates = useMemo(
    () => getOrderedMediaCandidates({ primaryUrl: src, backupUrl: backupSrc, preferenceKey }),
    [backupSrc, preferenceKey, src],
  );
  const candidateSignature = useMemo(() => candidates.map((item) => item.url).join('|'), [candidates]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setCurrentIndex(0);
  }, [candidateSignature]);

  useEffect(() => {
    if (candidates.length < 2 || currentIndex > 0) return undefined;

    timerRef.current = setTimeout(() => {
      setCurrentIndex(1);
      rememberMediaPriority(preferenceKey, 'backup');
      onError('timeout');
    }, timeoutMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [candidates, currentIndex, onError, preferenceKey, timeoutMs]);

  const activeCandidate = candidates[currentIndex] || null;
  if (!activeCandidate?.url) {
    return null;
  }

  return React.createElement('img', {
    ...props,
    alt: String(alt || ''),
    src: activeCandidate.url,
    onLoad: (event) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      rememberMediaPriority(preferenceKey, activeCandidate.role === 'backup' ? 'backup' : 'primary');
      onLoad(event);
    },
    onError: () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      if (currentIndex < candidates.length - 1) {
        const nextCandidate = candidates[currentIndex + 1];
        setCurrentIndex((prev) => prev + 1);
        rememberMediaPriority(preferenceKey, nextCandidate?.role === 'backup' ? 'backup' : 'primary');
      }

      onError('error');
    },
  });
}

